import type { Character } from '@cartwhl/common/api/getCharacters';

import type { MotionEditorState } from '../motionEditorStore';
import type {
  MotionEditorActor,
  MotionEditorCharacterSettings,
} from '../types';

interface MotionEditorActorTargets {
  activePlayerActorId?: string;
  cameraFitActorId?: string;
  cameraFitKey: string;
  sceneActorIds: string[];
}

export interface MotionEditorSceneActorRenderItem {
  actor: MotionEditorActor;
  animation?: {
    type: 'sequence';
    urls: string[];
    rotationOffsets: MotionEditorActor['rotationOffsets'];
  };
  characterKey: string;
  characterSettings: MotionEditorCharacterSettings;
}

export function selectSelectedSceneReference(state: MotionEditorState) {
  const { sceneActors, selectedReferenceName } = state.playback;

  return (
    sceneActors.find(
      (reference) => reference.referenceName === selectedReferenceName,
    ) ?? null
  );
}

export function selectSceneCharacterId(state: MotionEditorState) {
  const selectedSceneReference = selectSelectedSceneReference(state);

  return (
    selectedSceneReference?.characterID ??
    state.characters.selectedCharacter?.characterID ??
    state.characters.characterSettings.charUploadID
  );
}

export function selectSelectedCharacterMeta(
  state: MotionEditorState,
): Character | null {
  const sceneCharacterID = selectSceneCharacterId(state);
  if (!sceneCharacterID) {
    return null;
  }

  const aliases = new Set([
    sceneCharacterID,
    sceneCharacterID.replace(/_\d+$/, ''),
  ]);

  return (
    Object.entries(state.characters.sceneCharactersByID).find(
      ([characterID]) => aliases.has(characterID),
    )?.[1] ??
    state.characters.availableCharacters.data?.find((character) =>
      aliases.has(character.jobID),
    ) ??
    null
  );
}

export function selectMotionEditorActorTargets(
  state: MotionEditorState,
): MotionEditorActorTargets {
  const { scene, characters, playback } = state;
  const activePlayerActorId =
    playback.selectedReferenceName ?? characters.selectedCharacter?.characterID;
  const sceneActorIds = playback.sceneActors.map((actor) => actor.referenceName);

  return {
    activePlayerActorId,
    cameraFitActorId:
      activePlayerActorId ?? playback.sceneActors[0]?.referenceName,
    cameraFitKey: scene.data?.sceneID ?? 'motion-editor-fallback',
    sceneActorIds,
  };
}

export function selectSceneActorRenderItems(
  state: MotionEditorState,
): MotionEditorSceneActorRenderItem[] {
  return state.playback.sceneActors.map((actor) => ({
    actor,
    animation:
      actor.bvhUrls.length > 0
        ? {
            type: 'sequence',
            urls: actor.bvhUrls,
            rotationOffsets: actor.rotationOffsets,
          }
        : undefined,
    characterKey: `${actor.referenceName}:${actor.characterID}:${actor.bvhUrls.join('|')}`,
    characterSettings:
      state.characters.byReferenceName[actor.referenceName]?.characterSettings ??
      state.characters.characterSettings,
  }));
}
